import React from 'react';
import { Eye, Edit3, RefreshCw, Trash2 } from 'lucide-react';

const statusConfig = {
  draft: { labelEn: 'Draft', labelNe: 'मस्यौदा', class: 'bg-char-100 text-char-600' },
  pending: { labelEn: 'Pending', labelNe: 'समीक्षामा', class: 'bg-amber-50 text-amber' },
  approved: { labelEn: 'Approved', labelNe: 'स्वीकृत', class: 'bg-primary-100 text-primary' },
  published: { labelEn: 'Published', labelNe: 'प्रकाशित', class: 'bg-primary-500 text-white' },
  rejected: { labelEn: 'Rejected', labelNe: 'अस्वीकृत', class: 'bg-error-50 text-error' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('ne-NP', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const ReporterArticleTable = ({ articles, language, onView, onEdit, onResubmit, onDelete }) => {
  if (!articles || articles.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-3xl border border-char-100 bg-white py-12">
        <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-char-50">
          <svg className="h-7 w-7 text-char-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <p className="text-sm font-semibold text-char-500">
          {language === 'ne' ? 'कुनै लेख छैन' : 'No articles found'}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-3xl border border-char-100 bg-white shadow-card">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-char-100 bg-char-50">
            <th className="px-5 py-3.5 text-xs font-extrabold uppercase tracking-wider text-char-500">
              {language === 'ne' ? 'शीर्षक' : 'Title'}
            </th>
            <th className="px-5 py-3.5 text-xs font-extrabold uppercase tracking-wider text-char-500">
              {language === 'ne' ? 'क्षेत्र' : 'Sector'}
            </th>
            <th className="px-5 py-3.5 text-xs font-extrabold uppercase tracking-wider text-char-500">
              {language === 'ne' ? 'स्थिति' : 'Status'}
            </th>
            <th className="hidden px-5 py-3.5 text-xs font-extrabold uppercase tracking-wider text-char-500 md:table-cell">
              {language === 'ne' ? 'पेश मिति' : 'Submitted'}
            </th>
            <th className="hidden px-5 py-3.5 text-xs font-extrabold uppercase tracking-wider text-char-500 lg:table-cell">
              {language === 'ne' ? 'अद्यावधिक' : 'Updated'}
            </th>
            <th className="px-5 py-3.5 text-xs font-extrabold uppercase tracking-wider text-char-500">
              {language === 'ne' ? 'कार्य' : 'Action'}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-char-50">
          {articles.map((article) => {
            const status = statusConfig[article.status] || statusConfig.draft;
            return (
              <tr key={article._id} className="transition-colors hover:bg-cream-50">
                <td className="px-5 py-4">
                  <p className="font-bold text-char-900 line-clamp-1">{article.title}</p>
                  {article.excerpt && (
                    <p className="mt-0.5 text-xs text-char-400 line-clamp-1">{article.excerpt}</p>
                  )}
                </td>
                <td className="px-5 py-4">
                  <span className="text-sm text-char-600">{article.sector || article.category || '—'}</span>
                </td>
                <td className="px-5 py-4">
                  <span className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-wider ${status.class}`}>
                    {language === 'ne' ? status.labelNe : status.labelEn}
                  </span>
                </td>
                <td className="hidden px-5 py-4 text-sm text-char-500 md:table-cell">
                  {formatDate(article.createdAt)}
                </td>
                <td className="hidden px-5 py-4 text-sm text-char-500 lg:table-cell">
                  {formatDate(article.updatedAt)}
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => onView?.(article)}
                      className="rounded-lg p-1.5 text-char-400 transition-colors hover:bg-primary-100 hover:text-primary"
                      title={language === 'ne' ? 'हेर्नुहोस्' : 'View'}
                    >
                      <Eye size={16} />
                    </button>
                    {article.status !== 'published' && (
                      <button
                        onClick={() => onEdit?.(article)}
                        className="rounded-lg p-1.5 text-char-400 transition-colors hover:bg-amber-50 hover:text-amber"
                        title={language === 'ne' ? 'सम्पादन' : 'Edit'}
                      >
                        <Edit3 size={16} />
                      </button>
                    )}
                    {article.status === 'rejected' && (
                      <button
                        onClick={() => onResubmit?.(article)}
                        className="rounded-lg p-1.5 text-char-400 transition-colors hover:bg-primary-100 hover:text-primary"
                        title={language === 'ne' ? 'पुनः पेश गर्नुहोस्' : 'Resubmit'}
                      >
                        <RefreshCw size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => onDelete?.(article)}
                      className="rounded-lg p-1.5 text-char-400 transition-colors hover:bg-red-50 hover:text-red"
                      title={language === 'ne' ? 'मेटाउनुहोस्' : 'Delete'}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ReporterArticleTable;
